/**
 * Módulo de Caja, Cobro, Ticket y Corte de Caja.
 *
 * Maneja apertura/cierre de turnos con arqueo, cobro multi-método (efectivo con cambio,
 * terminal bancaria, transferencia, pago dividido), vista e impresión de ticket térmico
 * ESC/POS y corte de caja por método de pago.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { cantidad } from '../lib/formato';

interface TurnoCaja {
  id: number;
  fondo_inicial: number | string;
  estado: 'abierto' | 'cerrado';
  total_efectivo: number | string;
  total_terminal: number | string;
  total_transferencia: number | string;
  abierto_en: string;
}

interface MesaCobro {
  id: number;
  numero: number;
  nombre: string;
  estado: string;
  cuenta_activa_id: number | null;
  cuenta_total: number | string | null;
}

interface DetallePedidoMostrador {
  id: number;
  cantidad: number | string;
  subtotal: number | string;
  producto_nombre: string;
}

interface PedidoMostrador {
  id: number;
  numero_orden: number;
  estado: string;
  total: number | string;
  nombre_cliente: string;
  creado_en: string;
  detalles: DetallePedidoMostrador[];
}

interface ItemPagoInput {
  metodo: 'efectivo' | 'terminal' | 'transferencia';
  monto: number;
  entregado?: number;
  referencia?: string;
  banco?: string;
}

interface TicketData {
  cuenta: {
    id: number;
    mesa_numero: number;
    mesa_nombre: string;
    total: number | string;
    creado_en: string;
    ordenes: {
      id: number;
      numero_orden: number;
      detalles: {
        id: number;
        producto_nombre: string;
        cantidad: number;
        precio_unitario: number | string;
        subtotal: number | string;
        modificadores?: { nombre_opcion: string; precio_extra: number | string }[];
      }[];
    }[];
  };
  datosFiscales: {
    razon_social: string;
    rfc: string;
    regimen_fiscal: string;
    codigo_postal: string;
    direccion: string | null;
  } | null;
  pagos: { metodo: string; monto: number | string; cambio: number | string; referencia: string | null; banco: string | null }[];
}

export function Caja({ sucursalId }: { sucursalId: number }) {
  const [turno, setTurno] = useState<TurnoCaja | null>(null);
  const [cargandoTurno, setCargandoTurno] = useState(true);
  const [fondoInicialInput, setFondoInicialInput] = useState('500');

  // Qué lista se ve a la izquierda: mesas con cuenta abierta, o pedidos de mostrador
  const [vista, setVista] = useState<'mesas' | 'mostrador'>('mesas');

  // Mesas y cuentas
  const [mesas, setMesas] = useState<MesaCobro[]>([]);
  const [mesaSel, setMesaSel] = useState<MesaCobro | null>(null);
  const [detallesCuenta, setDetallesCuenta] = useState<TicketData['cuenta'] | null>(null);
  const [cargandoCuenta, setCargandoCuenta] = useState(false);

  // Pedidos de mostrador (para llevar)
  const [pedidosMostrador, setPedidosMostrador] = useState<PedidoMostrador[]>([]);
  const [pedidoSel, setPedidoSel] = useState<PedidoMostrador | null>(null);

  // Cobro
  const [mostrarModalCobro, setMostrarModalCobro] = useState(false);
  const [pagos, setPagos] = useState<ItemPagoInput[]>([
    { metodo: 'efectivo', monto: 0, entregado: 0 },
  ]);
  const [procesandoCobro, setProcesandoCobro] = useState(false);

  // Ticket Modal
  const [ticketModal, setTicketModal] = useState<TicketData | null>(null);

  // Corte de Caja Modal
  const [mostrarCierreModal, setMostrarCierreModal] = useState(false);
  const [efectivoContado, setEfectivoContado] = useState('');
  const [notasCierre, setNotasCierre] = useState('');
  const [procesandoCierre, setProcesandoCierre] = useState(false);

  const cargarTurno = useCallback(async () => {
    try {
      setCargandoTurno(true);
      const res = await api<TurnoCaja | null>(`/caja/turno?sucursalId=${sucursalId}`);
      setTurno(res);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoTurno(false);
    }
  }, [sucursalId]);

  const cargarMesas = useCallback(async () => {
    try {
      const res = await api<MesaCobro[]>(`/mesas?sucursalId=${sucursalId}`);
      setMesas(res.filter((m) => m.cuenta_activa_id !== null));
    } catch (err) {
      console.error(err);
    }
  }, [sucursalId]);

  const cargarPedidosMostrador = useCallback(async () => {
    try {
      const res = await api<PedidoMostrador[]>(`/caja/mostrador/pendientes?sucursalId=${sucursalId}`);
      setPedidosMostrador(res);
    } catch (err) {
      console.error(err);
    }
  }, [sucursalId]);

  useEffect(() => {
    cargarTurno();
    cargarMesas();
    cargarPedidosMostrador();
  }, [cargarTurno, cargarMesas, cargarPedidosMostrador]);

  async function abrirTurno(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await api<TurnoCaja>(`/caja/turno/abrir?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: { fondoInicial: Number(fondoInicialInput) || 0 },
      });
      setTurno(res);
    } catch (err: any) {
      alert(err?.message || 'Error al abrir turno.');
    }
  }

  async function seleccionarMesa(m: MesaCobro) {
    setPedidoSel(null);
    setMesaSel(m);
    if (!m.cuenta_activa_id) return;
    try {
      setCargandoCuenta(true);
      const res = await api<TicketData>(`/caja/ticket/${m.cuenta_activa_id}?sucursalId=${sucursalId}`);
      setDetallesCuenta(res.cuenta);
      // Pre-cargar cobro único en efectivo por defecto
      const tot = Number(res.cuenta.total || 0);
      setPagos([{ metodo: 'efectivo', monto: tot, entregado: tot }]);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoCuenta(false);
    }
  }

  function seleccionarPedido(p: PedidoMostrador) {
    setMesaSel(null);
    setDetallesCuenta(null);
    setPedidoSel(p);
    const tot = Number(p.total || 0);
    setPagos([{ metodo: 'efectivo', monto: tot, entregado: tot }]);
  }

  function agregarMetodoPago() {
    setPagos((prev) => [...prev, { metodo: 'terminal', monto: 0, banco: 'BBVA', referencia: '' }]);
  }

  function actualizarPago(idx: number, cambios: Partial<ItemPagoInput>) {
    setPagos((prev) => {
      const copia = [...prev];
      copia[idx] = { ...copia[idx], ...cambios };
      return copia;
    });
  }

  function eliminarMetodoPago(idx: number) {
    setPagos((prev) => prev.filter((_, i) => i !== idx));
  }

  const totalCuentaSel = useMemo(
    () => Number(detallesCuenta?.total || pedidoSel?.total || 0),
    [detallesCuenta, pedidoSel]
  );
  const sumaPagosInput = useMemo(() => pagos.reduce((s, p) => s + (Number(p.monto) || 0), 0), [pagos]);
  const cambioEfectivo = useMemo(() => {
    const pagoEf = pagos.find((p) => p.metodo === 'efectivo');
    if (!pagoEf) return 0;
    const ent = Number(pagoEf.entregado || 0);
    const mon = Number(pagoEf.monto || 0);
    return Math.max(0, ent - mon);
  }, [pagos]);

  async function ejecutarCobro() {
    if ((!mesaSel?.cuenta_activa_id && !pedidoSel) || procesandoCobro) return;

    if (Math.round(sumaPagosInput * 100) / 100 < Math.round(totalCuentaSel * 100) / 100) {
      alert(`El pago ($${sumaPagosInput.toFixed(2)}) no cubre el total de la cuenta ($${totalCuentaSel.toFixed(2)}).`);
      return;
    }

    try {
      setProcesandoCobro(true);

      if (pedidoSel) {
        await api(`/caja/mostrador/cobrar?sucursalId=${sucursalId}`, {
          method: 'POST',
          body: { ordenId: pedidoSel.id, pagos },
        });

        setMostrarModalCobro(false);
        alert(`Pedido #${pedidoSel.numero_orden} de ${pedidoSel.nombre_cliente} cobrado. ¡Listo para entregar!`);
        setPedidoSel(null);
        await Promise.all([cargarTurno(), cargarPedidosMostrador()]);
        return;
      }

      await api(`/caja/cobrar?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: {
          cuentaMesaId: mesaSel!.cuenta_activa_id,
          pagos,
        },
      });

      // Obtener datos del ticket para previsualizar/imprimir
      const ticketRes = await api<TicketData>(`/caja/ticket/${mesaSel!.cuenta_activa_id}?sucursalId=${sucursalId}`);

      setMostrarModalCobro(false);
      setTicketModal(ticketRes);
      setMesaSel(null);
      setDetallesCuenta(null);
      await Promise.all([cargarTurno(), cargarMesas()]);
    } catch (err: any) {
      alert(err?.message || 'Error al procesar cobro.');
    } finally {
      setProcesandoCobro(false);
    }
  }

  async function ejecutarCierreTurno(e: React.FormEvent) {
    e.preventDefault();
    if (!efectivoContado || procesandoCierre) return;

    try {
      setProcesandoCierre(true);
      const res = await api<TurnoCaja>(`/caja/turno/cerrar?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: {
          efectivoContado: Number(efectivoContado),
          notas: notasCierre,
        },
      });

      setMostrarCierreModal(false);
      setTurno(res);
      alert(`Turno cerrado. Diferencia de efectivo: $${Number(res.total_efectivo || 0).toFixed(2)}`);
      await cargarTurno();
    } catch (err: any) {
      alert(err?.message || 'Error al cerrar turno.');
    } finally {
      setProcesandoCierre(false);
    }
  }

  if (cargandoTurno) {
    return <p className="py-24 text-center text-salvia font-bold">Cargando estado de caja…</p>;
  }

  // Pantalla de apertura de turno si no hay uno abierto
  if (!turno || turno.estado === 'cerrado') {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center p-4">
        <form onSubmit={abrirTurno} className="w-full max-w-sm rounded-3xl border border-nopal/30 bg-white p-6 shadow-xl">
          <div className="text-center mb-4">
            <span className="text-4xl">💰</span>
            <h2 className="font-titulo text-xl font-bold text-tinta mt-2">Apertura de Turno de Caja</h2>
            <p className="text-xs text-salvia mt-1">Ingresa el fondo de caja inicial para comenzar el cobro</p>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-bold text-salvia">Fondo Inicial ($) *</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={fondoInicialInput}
              onChange={(e) => setFondoInicialInput(e.target.value)}
              className="campo text-lg font-bold text-nopal-oscuro"
            />
          </div>

          <button type="submit" className="w-full btn-principal py-3">
            Abrir Turno de Caja
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 font-cuerpo text-tinta">
      {/* Resumen de Turno Activo */}
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-salvia/20 bg-white p-5 shadow-sm">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-nopal-oscuro">Turno de Caja Activo</span>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Caja Principal</h1>
          <p className="text-xs text-salvia">Fondo inicial: ${Number(turno.fondo_inicial).toFixed(2)}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-3 text-xs font-bold">
            <div className="rounded-xl bg-nopal-claro p-2.5 text-nopal-oscuro">
              Efectivo: ${Number(turno.total_efectivo).toFixed(2)}
            </div>
            <div className="rounded-xl bg-miel/15 p-2.5 text-miel-oscuro">
              Terminal: ${Number(turno.total_terminal).toFixed(2)}
            </div>
            <div className="rounded-xl bg-cal border border-salvia/20 p-2.5 text-tinta">
              Transf: ${Number(turno.total_transferencia).toFixed(2)}
            </div>
          </div>

          <button
            onClick={() => setMostrarCierreModal(true)}
            className="rounded-xl border border-barro/40 bg-barro-claro px-4 py-2 text-xs font-bold text-barro-oscuro hover:bg-barro/20 transition"
          >
            Corte de Caja
          </button>
        </div>
      </header>

      {/* Grid: Lista de Cuentas a Cobrar + Detalle de Cuenta */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Columna Izquierda: Cuentas Pendientes */}
        <div className="lg:col-span-5">
          <div className="mb-3 flex gap-2">
            <button
              onClick={() => setVista('mesas')}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                vista === 'mesas' ? 'bg-nopal text-white' : 'bg-cal text-salvia hover:bg-salvia/10'
              }`}
            >
              Mesas ({mesas.length})
            </button>
            <button
              onClick={() => setVista('mostrador')}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                vista === 'mostrador' ? 'bg-nopal text-white' : 'bg-cal text-salvia hover:bg-salvia/10'
              }`}
            >
              🛍️ Para llevar ({pedidosMostrador.length})
            </button>
          </div>

          {vista === 'mesas' ? (
            mesas.length === 0 ? (
              <div className="rounded-2xl border border-salvia/20 bg-white p-8 text-center text-salvia">
                <p className="text-sm font-semibold">No hay mesas pendientes de cobro.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mesas.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => seleccionarMesa(m)}
                    className={`flex items-center justify-between rounded-2xl border p-4 cursor-pointer transition shadow-sm ${
                      mesaSel?.id === m.id
                        ? 'border-nopal bg-nopal-claro text-nopal-oscuro font-bold'
                        : 'border-salvia/20 bg-white hover:bg-cal/60'
                    }`}
                  >
                    <div>
                      <h3 className="font-titulo text-base font-bold">{m.nombre}</h3>
                      <span className="text-xs text-salvia">Mesa #{m.numero}</span>
                    </div>
                    <span className="font-titulo text-lg font-extrabold text-nopal-oscuro">
                      ${Number(m.cuenta_total || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )
          ) : pedidosMostrador.length === 0 ? (
            <div className="rounded-2xl border border-salvia/20 bg-white p-8 text-center text-salvia">
              <p className="text-sm font-semibold">No hay pedidos para llevar pendientes de cobro.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pedidosMostrador.map((p) => (
                <div
                  key={p.id}
                  onClick={() => seleccionarPedido(p)}
                  className={`flex items-center justify-between rounded-2xl border p-4 cursor-pointer transition shadow-sm ${
                    pedidoSel?.id === p.id
                      ? 'border-nopal bg-nopal-claro text-nopal-oscuro font-bold'
                      : 'border-salvia/20 bg-white hover:bg-cal/60'
                  }`}
                >
                  <div>
                    <h3 className="font-titulo text-base font-bold">{p.nombre_cliente}</h3>
                    <span className="text-xs text-salvia">
                      Pedido #{p.numero_orden} · {p.estado === 'lista' ? 'Listo' : p.estado.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="font-titulo text-lg font-extrabold text-nopal-oscuro">
                    ${Number(p.total || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Columna Derecha: Previsualización de Comanda y Cobro */}
        <div className="lg:col-span-7">
          {cargandoCuenta ? (
            <div className="rounded-2xl border border-salvia/20 bg-white p-12 text-center text-salvia font-bold">
              Cargando detalle de la cuenta…
            </div>
          ) : mesaSel && detallesCuenta ? (
            <div className="rounded-2xl border border-salvia/20 bg-white p-6 shadow-md">
              <div className="flex items-center justify-between border-b border-salvia/15 pb-3 mb-4">
                <div>
                  <h3 className="font-titulo text-xl font-bold text-tinta">{mesaSel.nombre}</h3>
                  <span className="text-xs text-salvia">Detalle de consumo</span>
                </div>
                <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                  ${totalCuentaSel.toFixed(2)}
                </span>
              </div>

              {/* Lista de platillos */}
              <div className="max-h-64 overflow-y-auto space-y-3 pr-1 mb-6">
                {detallesCuenta.ordenes.map((ord) => (
                  <div key={ord.id} className="rounded-xl border border-salvia/15 bg-cal/40 p-3">
                    <span className="text-xs font-bold text-salvia mb-1 block">Orden #{ord.numero_orden}</span>
                    {ord.detalles.map((d) => (
                      <div key={d.id} className="flex justify-between text-sm py-0.5">
                        <span>
                          <strong className="text-nopal-oscuro">{cantidad(d.cantidad)}x</strong> {d.producto_nombre}
                        </span>
                        <span className="font-semibold">${Number(d.subtotal).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setMostrarModalCobro(true)}
                className="w-full btn-principal py-3.5 text-lg font-bold"
              >
                💳 Cobrar Cuenta (${totalCuentaSel.toFixed(2)})
              </button>
            </div>
          ) : pedidoSel ? (
            <div className="rounded-2xl border border-salvia/20 bg-white p-6 shadow-md">
              <div className="flex items-center justify-between border-b border-salvia/15 pb-3 mb-4">
                <div>
                  <h3 className="font-titulo text-xl font-bold text-tinta">{pedidoSel.nombre_cliente}</h3>
                  <span className="text-xs text-salvia">
                    Pedido #{pedidoSel.numero_orden} para llevar · {pedidoSel.estado === 'lista' ? 'Listo para recoger' : pedidoSel.estado.replace('_', ' ')}
                  </span>
                </div>
                <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                  ${totalCuentaSel.toFixed(2)}
                </span>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-1 pr-1 mb-6">
                {pedidoSel.detalles.map((d) => (
                  <div key={d.id} className="flex justify-between text-sm py-0.5">
                    <span>
                      <strong className="text-nopal-oscuro">{cantidad(d.cantidad)}x</strong> {d.producto_nombre}
                    </span>
                    <span className="font-semibold">${Number(d.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setMostrarModalCobro(true)}
                className="w-full btn-principal py-3.5 text-lg font-bold"
              >
                💳 Cobrar y Entregar (${totalCuentaSel.toFixed(2)})
              </button>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-salvia/30 bg-cal/30 p-12 text-center text-salvia">
              <p className="text-base font-semibold">
                {vista === 'mesas' ? 'Selecciona una mesa para realizar el cobro.' : 'Selecciona un pedido para cobrarlo.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cobro Multi-método */}
      {mostrarModalCobro && (detallesCuenta || pedidoSel) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3 mb-4">
              <div>
                <h3 className="font-titulo text-xl font-bold text-tinta">
                  Cobrar {pedidoSel ? `pedido de ${pedidoSel.nombre_cliente}` : mesaSel?.nombre}
                </h3>
                <p className="text-xs text-salvia">Total a cobrar: ${totalCuentaSel.toFixed(2)}</p>
              </div>
              <button onClick={() => setMostrarModalCobro(false)} className="text-salvia hover:bg-cal p-1 rounded-full">
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[50vh] overflow-y-auto space-y-4 pr-1">
              {pagos.map((pago, idx) => (
                <div key={idx} className="rounded-2xl border border-salvia/20 bg-cal/40 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <select
                      value={pago.metodo}
                      onChange={(e) =>
                        actualizarPago(idx, { metodo: e.target.value as ItemPagoInput['metodo'] })
                      }
                      className="campo text-sm font-bold w-48"
                    >
                      <option value="efectivo">💵 Efectivo</option>
                      <option value="terminal">💳 Terminal Bancaria</option>
                      <option value="transferencia">📱 Transferencia</option>
                    </select>

                    {pagos.length > 1 && (
                      <button
                        onClick={() => eliminarMetodoPago(idx)}
                        className="text-xs font-bold text-barro underline"
                      >
                        Quitar
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-salvia mb-1">Monto a cubrir ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={pago.monto || ''}
                        onChange={(e) => actualizarPago(idx, { monto: Number(e.target.value) })}
                        className="campo text-base font-bold text-nopal-oscuro"
                      />
                    </div>

                    {pago.metodo === 'efectivo' && (
                      <div>
                        <label className="block text-xs font-bold text-salvia mb-1">Efectivo entregado ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pago.entregado || ''}
                          onChange={(e) => actualizarPago(idx, { entregado: Number(e.target.value) })}
                          className="campo text-base font-bold text-tinta"
                        />
                      </div>
                    )}

                    {pago.metodo === 'terminal' && (
                      <>
                        <div>
                          <label className="block text-xs font-bold text-salvia mb-1">Banco</label>
                          <input
                            type="text"
                            placeholder="Ej. BBVA / Banamex"
                            value={pago.banco || ''}
                            onChange={(e) => actualizarPago(idx, { banco: e.target.value })}
                            className="campo text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-salvia mb-1">Autorización / Referencia</label>
                          <input
                            type="text"
                            placeholder="Ej. 1234"
                            value={pago.referencia || ''}
                            onChange={(e) => actualizarPago(idx, { referencia: e.target.value })}
                            className="campo text-sm"
                          />
                        </div>
                      </>
                    )}

                    {pago.metodo === 'transferencia' && (
                      <div className="col-span-2">
                        <label className="block text-xs font-bold text-salvia mb-1">Número de Referencia</label>
                        <input
                          type="text"
                          placeholder="Ej. SPEI-987654"
                          value={pago.referencia || ''}
                          onChange={(e) => actualizarPago(idx, { referencia: e.target.value })}
                          className="campo text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={agregarMetodoPago}
                className="w-full rounded-xl border border-dashed border-salvia/40 py-2.5 text-xs font-bold text-salvia hover:bg-cal"
              >
                + Combinar otro método de pago (Pago Dividido)
              </button>
            </div>

            {/* Cálculo de cambio */}
            {cambioEfectivo > 0 && (
              <div className="mb-4 rounded-xl border border-nopal/30 bg-nopal-claro p-3 text-center">
                <span className="text-xs font-bold text-nopal-oscuro uppercase">Cambio a Entregar:</span>
                <div className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                  ${cambioEfectivo.toFixed(2)}
                </div>
              </div>
            )}

            <div className="border-t border-salvia/15 pt-3">
              <button
                disabled={procesandoCobro}
                onClick={ejecutarCobro}
                className="w-full btn-principal py-3.5 font-bold"
              >
                {procesandoCobro ? 'Procesando Cobro…' : 'Confirmar Cobro y Generar Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ticket Térmico Imprimible */}
      {ticketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <div id="seccion-ticket-impresion" className="font-mono text-xs text-tinta">
              <div className="text-center mb-3">
                <h2 className="font-titulo text-lg font-bold uppercase">{ticketModal.datosFiscales?.razon_social || 'COMANDA ERICMAR'}</h2>
                <p>RFC: {ticketModal.datosFiscales?.rfc || 'XAXX010101000'}</p>
                <p>CP: {ticketModal.datosFiscales?.codigo_postal || '00000'}</p>
                <p className="mt-1 font-bold">Mesa #{ticketModal.cuenta.mesa_numero}</p>
                <p className="text-[10px] text-salvia">{new Date().toLocaleString()}</p>
              </div>

              <div className="border-t border-b border-tinta/20 py-2 my-2 space-y-1">
                {ticketModal.cuenta.ordenes.flatMap((o) => o.detalles).map((d, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{cantidad(d.cantidad)}x {d.producto_nombre}</span>
                    <span>${Number(d.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-bold text-sm my-2">
                <span>TOTAL:</span>
                <span>${Number(ticketModal.cuenta.total).toFixed(2)}</span>
              </div>

              <div className="border-t border-tinta/20 pt-2 text-[10px] space-y-0.5">
                <p className="font-bold">MÉTODOS DE PAGO:</p>
                {ticketModal.pagos.map((p, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="capitalize">{p.metodo}</span>
                    <span>${Number(p.monto).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <button
                onClick={() => window.print()}
                className="w-full btn-principal py-2.5 text-sm"
              >
                🖨️ Imprimir Ticket (ESC/POS)
              </button>
              <button
                onClick={() => setTicketModal(null)}
                className="w-full rounded-xl border border-salvia/30 py-2.5 text-sm font-bold text-salvia"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Arqueo y Cierre de Turno */}
      {mostrarCierreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/70 p-4 backdrop-blur-sm">
          <form onSubmit={ejecutarCierreTurno} className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3 mb-4">
              <h3 className="font-titulo text-xl font-bold text-tinta">Corte de Caja</h3>
              <button type="button" onClick={() => setMostrarCierreModal(false)} className="text-salvia hover:bg-cal p-1 rounded-full">
                ✕
              </button>
            </div>

            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between border-b border-salvia/10 pb-1">
                <span>Fondo Inicial:</span>
                <span className="font-bold">${Number(turno.fondo_inicial).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-b border-salvia/10 pb-1 text-nopal-oscuro">
                <span>+ Efectivo Sistema:</span>
                <span className="font-bold">${Number(turno.total_efectivo).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-base pt-1">
                <span>= Efectivo Esperado en Cajón:</span>
                <span className="text-nopal-oscuro">
                  ${(Number(turno.fondo_inicial) + Number(turno.total_efectivo)).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-salvia mb-1">Efectivo Físico Contado en Cajón ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                placeholder="Ingresa el arqueo contado"
                value={efectivoContado}
                onChange={(e) => setEfectivoContado(e.target.value)}
                className="campo text-lg font-bold"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-salvia mb-1">Notas del Cierre de Caja (opcional)</label>
              <input
                type="text"
                placeholder="Ej. Faltante aclarado por vuelto..."
                value={notasCierre}
                onChange={(e) => setNotasCierre(e.target.value)}
                className="campo text-sm"
              />
            </div>

            <button disabled={procesandoCierre} type="submit" className="w-full btn-peligro py-3">
              {procesandoCierre ? 'Cerrando Turno…' : 'Confirmar Cierre de Caja'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
